// Sugalarm — periodic spoken status announcements (cycle 16; made
// resume-safe in cycle 309).
//
// When `enabled && intervalMinutes > 0`, speaks `getText()` every
// `intervalMinutes`. Does NOT speak immediately on mount — the first due
// time is one full interval after the timer arms. Suppresses ticks while
// a real alarm episode is active.
//
// Cycle 309 — the ROOT CAUSE of the "random utterance when opening the
// mobile app" report lived here: iOS WKWebView / Android WebView suspend
// JS timers while the app is backgrounded and fire the overdue callback
// IMMEDIATELY on foreground, so the old `setInterval` tick ran within a
// second of app open and spoke — often from the pre-background reading.
// The timer is now a cheap heartbeat against a WALL-CLOCK due time, and a
// due tick only speaks when the document is visible AND has been visible
// for a grace period. An overdue tick on resume is SKIPPED (the missed
// occurrence is not back-fired) and the clock re-arms one full interval
// out; `onSuppressedResume` lets the caller report the telemetry event.
// The kiosk (never backgrounded) keeps its cadence unchanged, modulo the
// ±HEARTBEAT_MS tick quantization.

// Cycle 330 — quiet hours is an ADDITIONAL suppression reason layered on
// top of the cycle-309 resume guards, not a replacement for them. It skips
// a scheduled speak exactly the way `urgentActive` already does. It gates
// only this cadence; alarms, alerts, and the manual Speak buttons are
// untouched by it (see `lib/quietHours.ts`).

import { useEffect, useRef } from "react";

import { isWithinQuietHours } from "../lib/quietHours";

export interface UseAutoAnnounceOpts {
  enabled: boolean;
  intervalMinutes: number;
  speak: (text: string) => void;
  getText: () => string;
  urgentActive: boolean;
  /** Cycle 330: the member's voice quiet-hours window. When `enabled` and
   *  `now` is inside [start, end), a due announcement is skipped. */
  quietHours?: {
    enabled: boolean;
    start: string;
    end: string;
  };
  /** Cycle 309: a due announcement was suppressed because the app just
   *  returned to the foreground (telemetry hook; optional). */
  onSuppressedResume?: () => void;
  /** Cycle 330: a due announcement was suppressed because quiet hours are
   *  open (telemetry hook; optional). Same reporting seam as above. */
  onSuppressedQuietHours?: () => void;
}

/** How often the heartbeat checks the wall-clock due time. */
const HEARTBEAT_MS = 15_000;
/** A due tick within this window of the app becoming visible is a
 *  suspended-timer catch-up fire, not a cadence tick — skip it. */
export const FOREGROUND_GRACE_MS = 15_000;

export function useAutoAnnounce(opts: UseAutoAnnounceOpts): void {
  const {
    enabled,
    intervalMinutes,
    speak,
    getText,
    urgentActive,
    quietHours,
    onSuppressedResume,
    onSuppressedQuietHours,
  } = opts;

  // Hold the latest callbacks in refs so the heartbeat effect re-arms
  // only on `enabled` / `intervalMinutes` / `urgentActive` changes.
  // `quietHours` rides a ref too: the window is evaluated AT TICK TIME, so
  // editing it in Settings takes effect on the next heartbeat without
  // re-arming (and therefore without shifting) the cadence.
  const speakRef = useRef(speak);
  const getTextRef = useRef(getText);
  const suppressedRef = useRef(onSuppressedResume);
  const quietHoursRef = useRef(quietHours);
  const suppressedQuietRef = useRef(onSuppressedQuietHours);
  speakRef.current = speak;
  getTextRef.current = getText;
  suppressedRef.current = onSuppressedResume;
  quietHoursRef.current = quietHours;
  suppressedQuietRef.current = onSuppressedQuietHours;

  // Wall-clock of the last hidden→visible transition (init: now — a cold
  // mount never fires inside the grace window anyway because the first
  // due time is a full interval out).
  const lastVisibleAtRef = useRef<number>(Date.now());
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      if (document.visibilityState === "visible") {
        lastVisibleAtRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (!enabled || intervalMinutes <= 0 || urgentActive) return;
    const intervalMs = intervalMinutes * 60_000;
    let nextDueAt = Date.now() + intervalMs;
    let lastTickAt = Date.now();
    const id = window.setInterval(() => {
      const now = Date.now();
      // The heartbeat itself was suspended (webview background): this
      // fire is a resume catch-up regardless of visibilitychange event
      // ordering. Detect via the gap since the previous tick.
      const heartbeatGap = now - lastTickAt;
      lastTickAt = now;
      if (now < nextDueAt) return;
      // Hidden: never speak into a backgrounded app; skip the occurrence.
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        nextDueAt = now + intervalMs;
        return;
      }
      // Suspended-timer catch-up on resume: skip + re-arm, never speak
      // at app open (cycle-309 operator rule). Two independent signals —
      // the visible-for grace window AND a stretched heartbeat gap.
      if (
        now - lastVisibleAtRef.current < FOREGROUND_GRACE_MS ||
        heartbeatGap > HEARTBEAT_MS * 3
      ) {
        nextDueAt = now + intervalMs;
        suppressedRef.current?.();
        return;
      }
      // Cycle 330: voice quiet hours. Skip the scheduled speak the same
      // way the guards above do — re-arm and stay silent, never back-fire
      // the missed announcement when the window closes. Evaluated at TICK
      // time against the device's local wall clock, so a window that opens
      // mid-cadence takes effect immediately.
      //
      // This gates the spoken cadence ONLY. It cannot reach an alarm, an
      // alert, a push, or the manual Speak Status button.
      const qh = quietHoursRef.current;
      if (
        qh?.enabled &&
        isWithinQuietHours(new Date(now), qh.start, qh.end)
      ) {
        nextDueAt = now + intervalMs;
        suppressedQuietRef.current?.();
        return;
      }
      nextDueAt = now + intervalMs;
      const text = getTextRef.current();
      if (text) speakRef.current(text);
    }, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [enabled, intervalMinutes, urgentActive]);
}
